import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, MapPin, Users, Sparkles } from "lucide-react";
import SEO from "../components/SEO";
import ReviewsSection from "../components/ReviewsSection";
import TryRiskFreeCTA from "../components/TryRiskFreeCTA";
import { fetchSiteCamps } from "../lib/api";
import { track } from "../lib/analytics";

function CampCard({ camp }) {
  const accent = camp.accent_color || "#C2410C";
  const secondary = camp.secondary_color || "#FFF1E8";
  return (
    <Link
      to={`/camps/${camp.slug}`}
      onClick={() => track("view_camp_clicked", { camp_slug: camp.slug })}
      className="group relative flex flex-col rounded-[1.75rem] overflow-hidden bg-white border border-slate-200/70 hover:border-slate-300 hover:shadow-md transition-all"
      data-testid={`camp-card-${camp.slug}`}
    >
      <span
        className="absolute top-0 left-0 right-0 h-1.5"
        style={{ backgroundColor: accent }}
      />
      <div className="relative aspect-[4/3] overflow-hidden">
        {camp.image_url ? (
          <img
            src={camp.image_url}
            alt={camp.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${secondary} 0%, #FFFFFF 80%)`,
            }}
          >
            <span
              className="font-display text-2xl font-extrabold tracking-tight px-6 text-center opacity-30"
              style={{ color: accent }}
            >
              {camp.name}
            </span>
          </div>
        )}
        <span
          className="absolute top-4 left-4 pill text-white text-[10px]"
          style={{ backgroundColor: accent }}
        >
          Seasonal
        </span>
        {camp.brand_label && (
          <span
            className="absolute bottom-4 left-4 pill text-[10px] backdrop-blur-sm"
            style={{ backgroundColor: "#FFFFFFCC", color: accent }}
          >
            {camp.brand_label}
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="pill text-[10px]"
            style={{ backgroundColor: secondary, color: accent }}
          >
            {camp.category || "Summer Camp"}
          </span>
          <span className="pill bg-slate-100 text-slate-600 text-[10px]">
            {camp.age_range || camp.ages || "All ages"}
          </span>
        </div>
        <h3 className="mt-4 font-display text-xl font-extrabold tracking-tight text-slate-900 leading-snug">
          {camp.name}
        </h3>
        <p className="mt-2 text-sm text-slate-600 line-clamp-3 leading-relaxed">
          {camp.description}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500">
          {camp.dates && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {camp.dates}
            </span>
          )}
          {camp.schedule && (
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {camp.schedule}
            </span>
          )}
        </div>
        <div
          className="mt-5 flex items-center gap-1.5 text-sm font-semibold transition-colors"
          style={{ color: accent }}
        >
          See camp details
          <ArrowUpRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

export default function CampsListPage() {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSiteCamps()
      .then((arr) => setCamps(arr || []))
      .catch(() => setCamps([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        path="/camps"
        title="STEM, Robotics & Brick-Based Engineering Camps for Kids in Richmond BC | Nexora"
        description="Seasonal camps in Richmond BC for kids 5–12. STEM, brick-based engineering, and robotics & coding camps with small groups and hands-on builds. Run by Nexora."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FFFCF7]" data-testid="camps-hero">
        <div className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full bg-orange-100/60 blur-[120px] opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-12">
          <span className="pill bg-white border border-slate-200 text-slate-700 shadow-sm">
            <Sparkles className="h-3 w-3 text-[#FF8C42]" />
            Nexora seasonal camps
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mt-5 max-w-3xl leading-[1.05]">
            STEM, Brick-Based & Robotics camps for{" "}
            <span className="text-[#C2410C]">curious kids.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Themed weekly camps in Richmond BC for kids 5–12. Small groups, hands-on builds,
            real engineering coaches. Based on the Young Engineers curriculum we run all year.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/trial-class" onClick={() => track("book_trial_clicked", { cta_target: "/trial-class" })} className="btn-primary" data-testid="camps-trial-cta">
              Book a trial class
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" onClick={() => track("request_info_clicked", { cta_target: "/contact" })} className="btn-soft" data-testid="camps-contact-cta">
              Ask about a camp week
            </Link>
          </div>
        </div>
      </section>

      {/* Camps grid */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14"
        data-testid="camps-grid"
      >
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div>
            <p className="eyebrow text-slate-500">Available camps</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-2">
              Pick the week that fits your child.
            </h2>
          </div>
          <p className="text-sm text-slate-500 inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            All camps run at the Nexora Learning Centre, Richmond BC
          </p>
        </div>
        {loading ? (
          <p className="text-slate-500 text-sm">Loading camps…</p>
        ) : camps.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
            <p className="font-display font-bold text-slate-900">
              No camps are currently published.
            </p>
            <p className="mt-2 text-sm text-slate-600">
              Drop us a message and we'll let you know the moment our next camp week opens.
            </p>
            <Link to="/contact" className="mt-5 btn-primary">
              Get notified
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {camps.map((c) => (
              <CampCard key={c.slug} camp={c} />
            ))}
          </div>
        )}
      </section>

      {/* Reviews */}
      <ReviewsSection />

      {/* Risk-free CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-6">
        <TryRiskFreeCTA
          title="Not sure which camp week fits?"
          description="Tell us your child's age and interests — we'll recommend the right camp week and answer any questions about schedule, snacks, or pickup."
          ctaLabel="Talk to Nexora"
          ctaTo="/contact"
        />
      </section>
    </>
  );
}
